import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faRocket,
    faShieldAlt,
    faHeadset,
    faArrowRight,
    faCalendarAlt,
    faStar
} from '@fortawesome/free-solid-svg-icons';
import PricingCard from '../../components/pricing/PricingCard';
import { get } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';

const PricingPage = () => {
    const [pricingConfig, setPricingConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState('peru'); // País por defecto
    const [selectedBillingPeriod, setSelectedBillingPeriod] = useState('monthly'); // Período de facturación por defecto
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const isLoggedIn = user && user.isAuthenticated;

    useEffect(() => {
        const fetchPricingConfig = async () => {
            try {
                const config = await get('payments/pricing-config');
                setPricingConfig(config);
            } catch (error) {
                console.error('Error fetching pricing config:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudo obtener la configuración de precios. Por favor, inténtelo más tarde.',
                    icon: 'error',
                    confirmButtonText: 'Aceptar',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchPricingConfig();
    }, []);

    const handleSelectPlan = (plan) => {
        if (!isLoggedIn) {
            Swal.fire({
                title: 'Acceso Denegado',
                text: 'Debes iniciar sesión para suscribirte a un plan.',
                icon: 'warning',
                confirmButtonText: 'Iniciar Sesión',
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/login');
                }
            });
            return;
        }

        navigate('/subscribe', { state: { plan } });
    };

    const handleCountryChange = (e) => {
        setSelectedCountry(e.target.value);
    };

    const handleBillingPeriodChange = (period) => {
        setSelectedBillingPeriod(period);
    };

    const benefits = [
        {
            icon: faRocket,
            title: "Implementación Rápida",
            description: "Configura tu programa de fidelización en menos de 30 minutos"
        },
        {
            icon: faShieldAlt,
            title: "Seguridad Garantizada",
            description: "Protección de datos y cumplimiento GDPR"
        },
        {
            icon: faHeadset,
            title: "Soporte 24/7",
            description: "Asistencia técnica disponible cuando la necesites"
        },
        {
            icon: faStar,
            title: "Funcionalidades Completas",
            description: "Acceso a todas las herramientas de fidelización"
        }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex justify-center items-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <div className="text-xl text-gray-600">Cargando planes...</div>
                </div>
            </div>
        );
    }

    if (!pricingConfig) {
        return (
            <div className="min-h-screen bg-gray-50 flex justify-center items-center">
                <div className="text-center">
                    <div className="text-xl text-gray-600">No se pudo cargar la configuración de precios.</div>
                </div>
            </div>
        );
    }

    // Obtener la configuración del país seleccionado
    const countryConfig = pricingConfig[selectedCountry];
    if (!countryConfig) {
        Swal.fire({
            title: 'Error',
            text: `No hay configuración para el país seleccionado: ${selectedCountry}`,
            icon: 'error',
            confirmButtonText: 'Aceptar',
        });
        return null;
    }

    // Asumimos que todas las configuraciones de planes en un país tienen la misma moneda
    const firstPlanKey = Object.keys(countryConfig).find((key) => key !== 'currency');
    const currency = firstPlanKey ? countryConfig[firstPlanKey].currency : 'USD';

    // Lista de países disponibles
    const availableCountries = Object.keys(pricingConfig);

    return (
        <div className="min-h-screen bg-gray-50">
            <Helmet>
                <title>
                    Precios Software de Fidelización – Planes de Programa de Lealtad Digital
                </title>
                <meta
                    name="description"
                    content="Conoce nuestros planes de software de fidelización, diseñados para pymes y grandes empresas. Compara características, precios y prueba gratis 30 días sin tarjeta de crédito."
                />
                <meta
                    name="keywords"
                    content="precios software de fidelización, planes de programa de lealtad digital, suscripción, demo, prueba gratis"
                />
            </Helmet>

            {/* Hero Section */}
            <header className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-20">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="text-5xl lg:text-6xl font-bold mb-6">
                        Planes y
                        <span className="text-yellow-400 block">Precios</span>
                    </h1>
                    <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
                        Elige el plan perfecto para tu negocio. Todos incluyen prueba gratuita de 30 días
                        sin necesidad de tarjeta de crédito.
                    </p>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 max-w-2xl mx-auto">
                        <div className="flex items-center justify-center text-blue-200">
                            <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-green-400" />
                            <span>Sin compromiso • Cancelación en cualquier momento • Soporte incluido</span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Benefits Section */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-800 mb-4">
                            ¿Por qué Elegir PetitPrime?
                        </h2>
                        <p className="text-xl text-gray-600">
                            Descubre las ventajas que nos hacen únicos en el mercado
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {benefits.map((benefit, index) => (
                            <div
                                key={index}
                                className="text-center p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 hover:shadow-lg transition-all duration-300"
                            >
                                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <FontAwesomeIcon icon={benefit.icon} className="text-2xl text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">{benefit.title}</h3>
                                <p className="text-gray-600">{benefit.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="max-w-7xl mx-auto">
                        {/* Selector de País */}
                        <div className="mb-8 flex justify-center">
                            <div className="bg-white rounded-2xl shadow-lg p-6">
                                <label htmlFor="country" className="mr-4 text-gray-700 font-semibold">
                                    Selecciona tu País:
                                </label>
                                <select
                                    id="country"
                                    value={selectedCountry}
                                    onChange={handleCountryChange}
                                    className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                >
                                    {availableCountries.map((country) => (
                                        <option key={country} value={country.toLowerCase()}>
                                            {country.charAt(0).toUpperCase() + country.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Opciones para Seleccionar Período de Facturación */}
                        <div className="flex justify-center mb-12">
                            <div className="bg-white rounded-2xl shadow-lg p-2">
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => handleBillingPeriodChange('monthly')}
                                        className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${selectedBillingPeriod === 'monthly'
                                                ? 'bg-blue-500 text-white shadow-lg'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        Mensual
                                    </button>
                                    <button
                                        onClick={() => handleBillingPeriodChange('annual')}
                                        className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${selectedBillingPeriod === 'annual'
                                                ? 'bg-blue-500 text-white shadow-lg'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        Anual
                                        <span className="ml-2 text-xs bg-green-500 text-white px-2 py-1 rounded-full">
                                            -20%
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Lista de Planes */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {Object.keys(countryConfig)
                                .filter((plan) => plan !== 'currency')
                                .map((plan) => (
                                    <PricingCard
                                        key={plan}
                                        plan={plan}
                                        price={countryConfig[plan]}
                                        currency={currency}
                                        billingPeriod={selectedBillingPeriod}
                                        features={countryConfig[plan].features}
                                        onSelect={handleSelectPlan}
                                        isCurrent={user?.plan === plan}
                                        isLoggedIn={isLoggedIn}
                                    />
                                ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-800 mb-4">
                            Preguntas Frecuentes
                        </h2>
                        <p className="text-xl text-gray-600">
                            Resolvemos tus dudas sobre nuestros planes
                        </p>
                    </div>
                    <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
                        <div className="bg-gray-50 rounded-2xl p-6">
                            <h3 className="text-lg font-bold text-gray-800 mb-3">¿Puedo cancelar en cualquier momento?</h3>
                            <p className="text-gray-600">Sí, puedes cancelar tu suscripción en cualquier momento sin penalización.</p>
                        </div>
                        <div className="bg-gray-50 rounded-2xl p-6">
                            <h3 className="text-lg font-bold text-gray-800 mb-3">¿Qué incluye la prueba gratuita?</h3>
                            <p className="text-gray-600">Acceso completo a todas las funcionalidades durante 30 días sin compromiso.</p>
                        </div>
                        <div className="bg-gray-50 rounded-2xl p-6">
                            <h3 className="text-lg font-bold text-gray-800 mb-3">¿Necesito tarjeta de crédito?</h3>
                            <p className="text-gray-600">No, puedes probar PetitPrime gratis sin necesidad de tarjeta de crédito.</p>
                        </div>
                        <div className="bg-gray-50 rounded-2xl p-6">
                            <h3 className="text-lg font-bold text-gray-800 mb-3">¿Incluye soporte técnico?</h3>
                            <p className="text-gray-600">Sí, todos los planes incluyen soporte técnico 24/7.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-4xl font-bold mb-6">
                        ¿Listo para Comenzar?
                    </h2>
                    <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
                        Únete a PetitPrime y transforma la fidelización de tus clientes
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/register"
                            className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold py-4 px-8 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
                        >
                            <FontAwesomeIcon icon={faRocket} className="mr-2" />
                            Comenzar Prueba Gratis
                            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                        </Link>
                        <Link
                            to="https://calendly.com/d/cpp2-rhr-c8q/reunion-de-30-minutos-petitprime"
                            className="border-2 border-white hover:bg-white hover:text-blue-600 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 flex items-center justify-center"
                        >
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                            Agenda una Reunión
                        </Link>
                    </div>
                    <div className="mt-8 text-blue-200 text-sm">
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-green-400" />
                        Sin compromiso • Configuración en 30 minutos • Soporte incluido
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PricingPage;
